import { keyBy, uniq, flatMap } from 'lodash';
import axios from 'axios';
import { getRoot, types, flow, Instance, cast } from 'mobx-state-tree';
import * as R from 'ramda';

import { IImjSettings } from '@src/features/candidates/features/settings/organisms/NotificationSettings';
import request from 'src/services/request';
import * as MeAPI from '@src/services/me';
import * as MeRecruitersAPI from '@src/services/freelancers';
import { composeFullUrl } from 'src/helpers';
import { ROUTES, USER_ROLES, APP_USER_PERMISSIONS, workPlaceLevels } from 'src/constants';
import { toast } from 'src/components';
import { IResourcesStore } from '../ResourcesStore';
import { GeneralUserModel } from './GeneralUserModel';

export enum AvailableForWorkStatus {
  actively_looking = 'actively_looking',
  open_to_opportunities = 'open_to_opportunities',
  not_looking = 'not_looking'
}

const paginationInitial = {
  page: types.number,
  page_count: types.number,
  per_page: types.number,
  total_count: types.number,
  links: { next: types.maybeNull(types.string) }
};

const pagination = types.frozen(paginationInitial);

export const UserStore = types
  .compose(
    GeneralUserModel,
    types.model('UserStore', {
      isFetching: 0,
      preferences: types.optional(
        types.model({
          roles: types.map(
            types.frozen({
              id: types.number,
              main_theme: types.frozen(),
              sub_themes: types.frozen(),
              focuses: types.frozen(),
              experience: types.string,
              salary: types.string,
              currency: types.string,
              edit_status: types.string
            })
          ),
          workPlaces: types.array(
            types.frozen({
              id: types.number,
              user_id: types.number,
              preferences_country_id: types.number,
              _destroy: types.boolean
            })
          ),
          visas: types.array(
            types.frozen({
              id: types.number,
              user_id: types.number,
              preferences_country_id: types.number,
              status: types.string,
              _destroy: types.boolean
            })
          )
        }),
        {}
      ),
      settings: types.maybeNull(
        types.model({
          follow_ups: types.boolean,
          newsletters: types.boolean
        })
      ),
      mailjet_lists: types.frozen(),
      invitations: types.optional(
        types.model({
          entities: types.array(
            types.frozen({
              id: types.number,
              job: types.frozen(),
              application: types.frozen()
            })
          ),
          pagination
        }),
        {
          entities: [],
          pagination: paginationInitial
        }
      ),
      applications: types.optional(
        types.model({
          entities: types.array(
            types.frozen({
              id: types.number,
              job: types.frozen()
            })
          ),
          pagination
        }),
        {
          entities: [],
          pagination: paginationInitial
        }
      ),
      interests: types.optional(
        types.model({
          entities: types.array(
            types.frozen({
              id: types.number,
              created_at: types.maybeNull(types.string),
              company: types.frozen(),
              offices: types.array(types.string),
              activities: types.array(types.string),
              internship: {
                start_date: types.string,
                end_date: types.string
              }
            })
          ),
          pagination
        }),
        {
          entities: [],
          pagination: paginationInitial
        }
      )
    })
  )
  .actions(self => {
    const CancelToken = axios.CancelToken;
    let source = CancelToken.source();

    return {
      // check if super admin or predicate
      orAdminPermission: function(predicate) {
        // (self.user && self.user.admin) => basically get isAdmin
        return (self.user && self.user.admin) || predicate();
      },
      abort: function() {
        source.cancel('Operation canceled by the user.');
        source = CancelToken.source();
      },
      refetchCv: flow(function*() {
        const response = yield MeAPI.getUserInfo();
        self.user = {
          ...self.user,
          resume_updated_at: response.resume_updated_at,
          resume_filename: response.resume_filename,
          resume_url: response.resume_url
        };
      }),
      fetchUser: flow(function*() {
        self.isFetching = self.isFetching + 1;
        try {
          const response = yield MeAPI.getUserInfo();
          try {
            // dont update resume in store if it's date newer then recieved from API
            // see https://meyvndigital.atlassian.net/browse/CA-1340
            if (self.user && response && new Date(self.user.resume_updated_at) > new Date(response.resume_updated_at)) {
              // prevent rejection if user don't match the MobX schema
              self.user = {
                ...self.user,
                ...response,
                resume_updated_at: self.user.resume_updated_at,
                resume_filename: self.user.resume_filename,
                resume_url: self.user.resume_url
              };
            } else {
              self.user = { ...self.user, ...response };
            }
          } catch (error) {
            console.error(error);
          }
        } finally {
          self.isFetching = self.isFetching - 1;
        }
      }),
      updateUser: flow(function*(...args: Parameters<typeof MeAPI.updateUser>) {
        const response = yield MeAPI.updateUser(...args);
        self.user = response.entity;
        return response;
      }),
      updateRecruiterUser: flow(function*(...args: Parameters<typeof MeAPI.updateUser>) {
        const response = yield MeRecruitersAPI.updateUser(...args);
        self.user = response.entity;
        return response;
      }),
      updateRecruiterPreferences: flow(function*(payload) {
        try {
          yield MeRecruitersAPI.updateUserPreferences(payload);
          toast.info('Your preferences have been updated');
        } catch (error) {}
      }),
      getRecruiterPreferences: flow(function*() {
        try {
          const res = yield MeRecruitersAPI.getUserPreferences();
          return res;
        } catch (error) {
          return [];
        }
      }),
      setUserInfo: payload => {
        self.user = payload && {
          ...(self.user || {}),
          ...payload
        };
      },
      deleteUser: flow(function*() {
        const { authStore } = getRoot(self);
        yield MeAPI.deleteUser();
        self.user = null;
        authStore.setIsAuthorized(false);
      }),
      getSettings: flow(function*() {
        self.isFetching = self.isFetching + 1;
        try {
          const res = yield MeAPI.getSettings();
          self.settings = res.email_notifications;
          self.mailjet_lists = res.mailjet_lists || [];
        } finally {
          self.isFetching = self.isFetching - 1;
        }
      }),
      updateSettings: flow(function*(...args: Parameters<typeof MeAPI.updateSettings>) {
        try {
          yield MeAPI.updateSettings(...args);
          if (args[0].email_notifications) {
            self.settings = args[0].email_notifications;
          }
          toast.info('Your settings have been updated');
        } catch (error) {}
      }),

      updateNotificationSettings: flow(function*({
        follow_ups,
        newsletters,
        mailjet_lists
      }: {
        follow_ups?: boolean;
        newsletters?: boolean;
        mailjet_lists?: IImjSettings;
      }) {
        const payload = { follow_ups, newsletters };
        const mjList = mailjet_lists || self.mailjet_lists || [];
        yield MeAPI.updateSettings({ email_notifications: payload, mailjet_lists: mjList });
        toast.info('Your settings have been updated');
        self.settings = payload;
        self.mailjet_lists = mjList;
      }),
      getFavoriteRoles: flow(function*() {
        self.isFetching = self.isFetching + 1;
        try {
          const response = yield MeAPI.getFavoriteRoles();
          self.preferences.roles = keyBy(response.map(castFavoriteRoleToObject), 'id');
        } finally {
          self.isFetching = self.isFetching - 1;
        }
      }),
      createFavoriteRole: flow(function*(favoriteRole) {
        const { resourcesStore } = getRoot<{ resourcesStore: IResourcesStore }>(self);
        const { entity } = yield MeAPI.createFavoriteRole(favoriteRole);
        self.preferences.roles?.set(entity.id, castFavoriteRoleToObject(entity));
      }),
      updateFavoriteRole: flow(function*(favoriteRole) {
        const { resourcesStore } = getRoot<{ resourcesStore: IResourcesStore }>(self);
        const { entity, preferences } = yield MeAPI.updateFavoriteRole(favoriteRole);
        self.preferences.roles?.set(entity.id, castFavoriteRoleToObject(entity));
        resourcesStore.updateThemes(preferences);
      }),
      deleteFavoriteRole: flow(function*(favoriteRoleId) {
        yield MeAPI.deleteFavoriteRole(favoriteRoleId);
        self.preferences.roles?.delete(favoriteRoleId);
      }),
      getUserWorkplaces: flow(function*() {
        self.isFetching = self.isFetching + 1;
        const response = yield MeAPI.getUserWorkPlaces({ cancelToken: source.token });
        self.preferences.workPlaces = response;
        self.isFetching = self.isFetching - 1;
      }),
      updateUserWorkplaces: flow(function*(payload) {
        try {
          const { work_places, user_visas } = yield MeAPI.updateUserWorkplaces(payload);
          self.preferences.workPlaces = work_places;
          self.preferences.visas = user_visas;
          toast.info('Success');
        } catch (error) {}
      }),
      getUserVisas: flow(function*() {
        self.isFetching = self.isFetching + 1;
        const response = yield MeAPI.getUserVisas({ cancelToken: source.token });
        self.preferences.visas = response;
        self.isFetching = self.isFetching - 1;
      }),

      acceptInvitation: flow(function*(...args: Parameters<typeof MeAPI.acceptInvitation>) {
        yield MeAPI.acceptInvitation(...args);
        self.invitations.entities = cast(self.invitations.entities.filter(invitation => invitation.id !== args[0]));
        toast.info('Success');
      }),
      getApplications: flow(function*(params = {}) {
        self.isFetching = self.isFetching + 1;
        const res = yield MeAPI.getApplications({
          cancelToken: source.token,
          params
        });
        const { applications, interests, invitations } = res || {};
        self.interests = interests;
        self.invitations = invitations;
        self.applications = applications;
        self.isFetching = self.isFetching - 1;
      }),
      getMoreApplications: flow(function*(link) {
        self.isFetching = self.isFetching + 1;
        const data = yield request.get(link);
        Object.keys(data).forEach(key => {
          if (!R.isEmpty(data[key])) {
            self[key].entities = cast([...self[key].entities, ...data[key].entities]);
            self[key].pagination = data[key].pagination;
          }
        });

        self.isFetching = self.isFetching - 1;
      }),
      withdrawApplication: flow(function*(payload: { id: number; rating?: number; feedback?: string }) {
        const response = yield MeAPI.withdrawApplication({
          application_id: payload.id,
          rating: payload.rating,
          feedback: payload.feedback
        });
        self.applications.entities = cast(
          self.applications.entities.filter(application => application.id !== payload.id)
        );
        return response;
      }),
      withdrawInterest: flow(function*(interestId: number) {
        const response = yield MeAPI.withdrawApplication({ interest_id: interestId });
        self.interests.entities = cast(self.interests.entities.filter(interest => interest.id !== interestId));
        return response;
      }),
      updateStepFeedback: flow(function* (applicationId: number, stepId: number, rating: number, continueApplication: boolean, feedback?: string) {
        yield MeAPI.updateStepFeedback(applicationId, { step_id: stepId, rating, feedback, continue_application: continueApplication });
      }),
      updatePhoto: flow(function*(payload: FormData | null) {
        if (payload === null) {
          const formData = new FormData();
          formData.append('remove_photo', true);
          const response = yield MeAPI.updatePhoto(formData);
          self.user = { ...self.user, ...response };
        } else {
          const response = yield MeAPI.updatePhoto(payload);
          self.user = { ...self.user, ...response };
        }
      }),
      updateCV: flow(function*(...args: Parameters<typeof MeAPI.updateCV>) {
        const response = yield MeAPI.updateCV(...args);
        self.user.resume_url = response.resume_url;
        self.user.resume_filename = response.filename;
        return response;
      })
    };
  })
  .views(self => ({
    get isEmployer() {
      return self.user?.role === USER_ROLES.employer;
    },
    get isCandidate() {
      return self.user && self.user.role === USER_ROLES.candidate;
    },
    get isFreelancer() {
      return self.user?.role === USER_ROLES.freelancer;
    },
    // super-duper admin
    get isAdmin() {
      return self.user && self.user.admin;
    },
    // admin for the company
    get isCompanyAdmin() {
      return self.user?.permission === APP_USER_PERMISSIONS.ADMIN;
    },
    get role() {
      return self.user?.role;
    },
    get isSainooTeam() {
      return self.user?.sainoo_team;
    },
    get defaultPrivateRoute() {
      const { routerStore } = getRoot(self);
      if (this.isEmployer) {
        const slug: any = self.user?.company?.slug;
        if (routerStore.isCompanyRoute) {
          return ROUTES.defaultEmployerPrivateRoute;
        }
        return composeFullUrl(ROUTES.defaultEmployerPrivateRoute, slug);
      }
      if (this.isCandidate) {
        if (!self.preferences.roles.size) {
          return ROUTES.candidatesRoutes.preferences.roles;
        }
        if (!self.user.languages?.length || !self.user.experiences?.length || self.user.degrees?.length) {
          return ROUTES.candidatesRoutes.profile.base;
        }
        return ROUTES.resources;
      }
      if (this.isFreelancer) {
        return ROUTES.defaultFreelancerPrivateRoute;
      }
      return ROUTES.home;
    },
    get defaultProfileRoute() {
      if (this.isCandidate) {
        return ROUTES.candidatesRoutes.profile.base;
      }
      return ROUTES.profile.base;
    }
  }))
  .views(self => {
    return {
      get isUserActive() {
        // check if user activated his email
        // return bool or null if role is undefined
        switch (self.role) {
          case USER_ROLES.employer:
            return self.user.is_employer_confirmed;
          //  default true for other roles(not employers)
          case USER_ROLES.candidate:
            return true;
          case USER_ROLES.freelancer:
            return true;
          case USER_ROLES.university:
            return true;

          default:
            return null;
        }
      },
      get favoriteRoles() {
        const favoriteRoles = Object.values(self.preferences.roles.toJSON()).map(favoriteRole => ({
          ...favoriteRole,
          main_theme: {
            ...favoriteRole.main_theme,
            theme_id: favoriteRole?.main_theme?.id
          }
        }));
        return favoriteRoles;
      },
      get workPlaces() {
        const rootStore = getRoot<{ resourcesStore: IResourcesStore }>(self);
        const { resourcesStore } = rootStore;
        const { workPlacesMap } = resourcesStore;
        const directParentsIds = self.preferences.workPlaces
          .map(({ preferences_country_id }) => workPlacesMap[preferences_country_id].parent_id)
          .filter(Boolean);
        const allParents = uniq(flatMap(directParentsIds, id => getParents(id, workPlacesMap)));
        const allUserUnions = allParents.filter(parent => parent.level === workPlaceLevels.union);
        const allUserCountries = allParents.filter(parent => parent.level === workPlaceLevels.country);
        const allUserCitiesById = keyBy(
          self.preferences.workPlaces.map(city => workPlacesMap[city.preferences_country_id]),
          'id'
        );
        const userVisasByUnionId = keyBy(self.preferences.visas, 'preferences_country_id');

        const workPlaces = allUserUnions.map(union => {
          const userCountriesOfUnion = allUserCountries.filter(country => country.parent_id === union.id);
          return {
            union,
            countries: userCountriesOfUnion,
            cities: [union, ...userCountriesOfUnion]
              .flatMap(item => item.children)
              .filter(id => workPlacesMap[id].level === workPlaceLevels.city && allUserCitiesById[id])
              .map(id => workPlacesMap[id]),
            visa: userVisasByUnionId[union.id]
          };
        });
        return workPlaces.length ? { workPlaces, visasToDelete: {} } : null;
      }
    };
  });

const getParents = (id, workPlacesMap) => {
  const parent = workPlacesMap[id];
  const grandParent = workPlacesMap[parent.parent_id];
  return [parent, grandParent].filter(Boolean);
};

const castFavoriteRoleToObject = favoriteRole => ({
  ...favoriteRole,
  sub_themes: keyBy(
    favoriteRole.sub_themes.map((item, order) => ({ ...item, order })),
    'theme_id'
  ),
  focuses: keyBy(
    favoriteRole.focuses.map((item, order) => ({ ...item, order })),
    'theme_id'
  ),
  _destroy: false
});

export type IUser = Instance<typeof GeneralUserModel>['user'];
export type IUserStore = Instance<typeof UserStore>;
